import { HttpErrorResponse } from '@angular/common/http';
import { SecurityQuestions } from '@shared/models/security-questions';
import { ValidationApiError } from '@shared/models/validation-api-error';
import * as userPasswordActions from '../actions/user-password.actions';

export interface ChangePassword {
  loaded: boolean;
  loading: boolean;
  changePasswordFailure: HttpErrorResponse | null;
}

export interface Questions extends SecurityQuestions {
  loaded: boolean;
  loading: boolean;
}

export interface UserPasswordState {
  resetPasswordError?: HttpErrorResponse;
  setNewPasswordError?: HttpErrorResponse;
  securityQuestions: Questions;
  changePassword: ChangePassword;
  challengeQuestion: string;
  validationErrors: ValidationApiError[];
  activated: boolean;
}

export const initialState: UserPasswordState = {
  securityQuestions: {
    questionCount: 0,
    questions: [],
    loading: false,
    loaded: false,
  },
  changePassword: {
    loading: false,
    loaded: false,
    changePasswordFailure: null,
  },
  activated: false,
  validationErrors: [],
  challengeQuestion: '',
};

export function reducer(
  state: UserPasswordState = initialState,
  action: userPasswordActions.UserPasswordAction
): UserPasswordState {
  switch (action.type) {
    /**
     * Get security questions
     */
    case userPasswordActions.GET_SECURITY_QUESTIONS: {
      return {
        ...state,
        securityQuestions: {
          ...state.securityQuestions,
          loading: true,
        },
      };
    }

    case userPasswordActions.GET_SECURITY_QUESTIONS_SUCCESS: {
      return {
        ...state,
        securityQuestions: {
          ...state.securityQuestions,
          loading: false,
          loaded: true,
          questions: action.payload.questions,
          questionCount: action.payload.questionCount,
        },
      };
    }

    case userPasswordActions.GET_SECURITY_QUESTIONS_FAIL: {
      return {
        ...state,
        securityQuestions: {
          ...state.securityQuestions,
          loading: false,
          loaded: false,
        },
      };
    }

    /**
     * Reset password
     */
    case userPasswordActions.RESET_PASSWORD_SUCCESS: {
      return {
        ...state,
        validationErrors: [],
        resetPasswordError: undefined,
      };
    }

    case userPasswordActions.RESET_PASSWORD_FAIL: {
      return {
        ...state,
        resetPasswordError: {
          ...action.payload,
        },
      };
    }

    /**
     * Get challenge question
     */
    case userPasswordActions.GET_CHALLENGE_QUESTION_SUCCESS: {
      return {
        ...state,
        challengeQuestion: action.payload,
        validationErrors: [],
      };
    }

    case userPasswordActions.GET_CHALLENGE_QUESTION_FAIL: {
      return {
        ...state,
        validationErrors: action.payload as ValidationApiError[],
      };
    }

    /**
     * Set new password
     */
    case userPasswordActions.SET_NEW_PASSWORD_SUCCESS: {
      return {
        ...state,
        setNewPasswordError: undefined,
      };
    }

    case userPasswordActions.SET_NEW_PASSWORD_FAIL: {
      return {
        ...state,
        setNewPasswordError: {
          ...action.payload,
        },
      };
    }

    /**
     * Change password
     */
    case userPasswordActions.CHANGE_PASSWORD: {
      return {
        ...state,
        changePassword: {
          ...state.changePassword,
          loading: true,
        },
      };
    }

    case userPasswordActions.CHANGE_PASSWORD_SUCCESS: {
      return {
        ...state,
        changePassword: {
          ...state.changePassword,
          loading: false,
          loaded: true,
        },
      };
    }

    case userPasswordActions.CHANGE_PASSWORD_FAIL: {
      return {
        ...state,
        changePassword: {
          loading: false,
          loaded: false,
          changePasswordFailure: action.payload,
        },
      };
    }

    case userPasswordActions.CHANGE_PASSWORD_REFRESH: {
      return {
        ...state,
        changePassword: initialState.changePassword,
      };
    }

    /**
     * Activate User
     */
    case userPasswordActions.ACTIVATE_USER: {
      return {
        ...state,
        validationErrors: [],
      };
    }

    case userPasswordActions.ACTIVATE_USER_SUCCESS: {
      return {
        ...state,
        activated: true,
      };
    }

    case userPasswordActions.ACTIVATE_USER_FAIL: {
      return {
        ...state,
        validationErrors: action.payload,
      };
    }

    default:
      return state;
  }
}

export const getSecurityQuestions = (state: UserPasswordState) => state.securityQuestions;
export const getChallengeQuestion = (state: UserPasswordState) => state.challengeQuestion;
export const getSetNewPasswordError = (state: UserPasswordState) => state.setNewPasswordError;
export const getChangePasswordLoading = (state: UserPasswordState) => state.changePassword.loading;
export const getChangePasswordSuccess = (state: UserPasswordState) => state.changePassword.loaded;
export const getChangePasswordFailure = (state: UserPasswordState) => state.changePassword.changePasswordFailure;
export const getSetPasswordFailure = (state: UserPasswordState) => state.setNewPasswordError;
export const getValidationErrors = (state: UserPasswordState) => state.validationErrors;
